import { can, canSeeLoadRates } from '../../supplementary_components/userDataDelegate';

export const DISPATCH_PERFORMANCE_LOADS_KEY = 'dispatchPerformance.loads';
export const TRUCK_PERFORMANCE_KEY = 'truckPerformance.columns';
export const TRUCK_PERFORMANCE_LOADS_KEY = 'truckPerformance.loads';

export const TRUCK_PERFORMANCE_LOAD_COLUMNS = [
    { key: 'loadNumber', label: 'Load #', locked: true, pin: 'start', defaultOn: true, type: 'loadLink', grid: 'minmax(8rem, 1.1fr)' },
    { key: 'states', label: 'States', defaultOn: true, type: 'states', grid: 'minmax(6rem, 0.8fr)' },
    { key: 'miles', label: 'Miles', defaultOn: false, type: 'miles', grid: 'minmax(7.5rem, 0.8fr)' },
    { key: 'payout', label: 'Payout', defaultOn: false, type: 'money', align: 'right', requiresDriverPayout: true, grid: 'minmax(6.5rem, 0.7fr)' },
    { key: 'rate', label: 'Rate', defaultOn: true, type: 'money', align: 'right', grid: 'minmax(6.5rem, 0.8fr)' },
];

export const isLegacyTruckLoadColumns = (keys) => {
    const ordered = orderKeysByCatalog(keys, TRUCK_PERFORMANCE_LOAD_COLUMNS);
    return ordered.length === 3
        && ordered[0] === 'loadNumber'
        && ordered[1] === 'states'
        && ordered[2] === 'rate';
};

export const TRUCK_PERFORMANCE_COLUMNS = [
    { key: 'truckNumber', label: 'Truck Number', locked: true, pin: 'start', defaultOn: true, type: 'text' },
    { key: 'totalGross', label: 'Total Gross', defaultOn: true, type: 'money' },
    { key: 'mileage', label: 'Odometer Miles', defaultOn: true, type: 'miles' },
    { key: 'rpm', label: 'RPM', defaultOn: true, type: 'rpm' },
    { key: 'payoutGross', label: 'Payout Gross', defaultOn: false, type: 'money', requiresDriverPayout: true },
    { key: 'payoutLoadRpm', label: 'Payout/Load RPM', defaultOn: false, type: 'rpm', requiresDriverPayout: true },
    { key: 'payoutOdoRpm', label: 'Payout/Odo RPM', defaultOn: false, type: 'rpm', requiresDriverPayout: true },
    { key: 'activeDays', label: 'Active Days', defaultOn: true, type: 'days' },
    { key: 'truckType', label: 'Truck Type', defaultOn: true, type: 'text' },
    { key: 'availableDays', label: 'Available Days', defaultOn: true, type: 'days' },
    { key: 'loadCount', label: 'Load Count', defaultOn: true, type: 'count' },
];

export const DISPATCH_LOAD_COLUMNS = [
    { key: 'loadNumber', label: 'Load #', locked: true, pin: 'start', defaultOn: true, type: 'loadLink', grid: 'minmax(6.5rem, 0.8fr)' },
    { key: 'brokerageName', label: 'Brokerage', defaultOn: false, type: 'text', grid: 'minmax(8rem, 1.2fr)' },
    { key: 'location', label: 'Start–End Location', defaultOn: false, type: 'location', grid: 'minmax(12rem, 1.6fr)' },
    { key: 'states', label: 'Start–End States', defaultOn: true, type: 'states', grid: 'minmax(6rem, 0.7fr)' },
    { key: 'totalMiles', label: 'Total Miles', defaultOn: false, type: 'miles', align: 'right', grid: 'minmax(6rem, 0.7fr)' },
    { key: 'totalRpm', label: 'Total RPM', defaultOn: false, type: 'rpm', align: 'right', requiresRates: true, milesKey: 'totalMiles', grid: 'minmax(6.5rem, 0.75fr)' },
    { key: 'loadedMiles', label: 'Loaded Miles', defaultOn: false, type: 'miles', align: 'right', grid: 'minmax(6.5rem, 0.7fr)' },
    { key: 'loadedRpm', label: 'Loaded RPM', defaultOn: false, type: 'rpm', align: 'right', requiresRates: true, milesKey: 'loadedMiles', grid: 'minmax(6.5rem, 0.75fr)' },
    { key: 'driverPayout', label: 'Driver Payout', defaultOn: false, type: 'driverPayout', align: 'right', requiresRates: true, requiresDriverPayout: true, grid: 'minmax(12rem, 0.8fr)' },
    { key: 'amountForInvoice', label: 'Rate', locked: true, pin: 'end', defaultOn: true, type: 'money', align: 'right', requiresRates: true, grid: 'minmax(6.5rem, 0.8fr)' },
];

export const buildReportColumnAccessOpts = () => ({
    userCanSeeRates: canSeeLoadRates(),
    userCanSeeDriverPayouts: can('DRIVER_PAYOUTS', 'VIEW'),
});

export const getVisibleReportColumnCatalog = (catalog, accessOpts = buildReportColumnAccessOpts()) => {
    if (!Array.isArray(catalog)) return [];
    return catalog.filter((col) => {
        if (col.requiresRates && !accessOpts.userCanSeeRates) return false;
        if (col.requiresDriverPayout && !accessOpts.userCanSeeDriverPayouts) return false;
        return true;
    });
};

const pinLockedColumnKeys = (keys, catalog) => {
    const byKey = new Map((catalog || []).map((col) => [col.key, col]));
    const start = [];
    const middle = [];
    const end = [];
    const seen = new Set();
    for (const key of keys || []) {
        if (seen.has(key) || !byKey.has(key)) continue;
        seen.add(key);
        const pin = byKey.get(key).pin;
        if (pin === 'end') end.push(key);
        else if (pin === 'start') start.push(key);
        else middle.push(key);
    }
    for (const col of catalog || []) {
        if (!col.locked || seen.has(col.key)) continue;
        seen.add(col.key);
        if (col.pin === 'end') end.push(col.key);
        else start.push(col.key);
    }
    return [...start, ...middle, ...end];
};

export const getDefaultReportColumns = (catalog, accessOpts = buildReportColumnAccessOpts()) => {
    const visibleCatalog = getVisibleReportColumnCatalog(catalog, accessOpts);
    return pinLockedColumnKeys(
        visibleCatalog.filter((col) => col.defaultOn || col.locked).map((col) => col.key),
        visibleCatalog
    );
};

export const orderKeysByCatalog = (keys, catalog) => {
    const order = new Map((catalog || []).map((col, index) => [col.key, index]));
    return [...(keys || [])].sort((a, b) => (order.get(a) ?? Number.MAX_SAFE_INTEGER) - (order.get(b) ?? Number.MAX_SAFE_INTEGER));
};

export const resolveVisibleCatalog = (visibleKeys, catalog) => {
    const byKey = new Map((catalog || []).map((col) => [col.key, col]));
    return (visibleKeys || []).map((key) => byKey.get(key)).filter(Boolean);
};

/** Drop unknown/gated keys; Load # first, Rate last; no saved prefs → defaults. */
export const normalizeReportColumns = (rawColumns, catalog, accessOpts = buildReportColumnAccessOpts()) => {
    const visibleCatalog = getVisibleReportColumnCatalog(catalog, accessOpts);
    const allowed = new Set(visibleCatalog.map((col) => col.key));
    const defaults = getDefaultReportColumns(catalog, accessOpts);

    if (!Array.isArray(rawColumns) || rawColumns.length === 0) {
        return defaults;
    }

    const selected = [];
    const seen = new Set();
    for (const key of rawColumns) {
        if (!allowed.has(key) || seen.has(key)) continue;
        seen.add(key);
        selected.push(key);
    }

    if (selected.length === 0) return defaults;
    return pinLockedColumnKeys(selected, visibleCatalog);
};

export const retrieveReportCustomizations = () => {
    try {
        const raw = localStorage.getItem('reportCustomizations');
        if (raw == null || raw === '' || raw === 'undefined') return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (_) {
        return {};
    }
};

export const persistReportCustomizations = (customizations) => {
    const next = customizations && typeof customizations === 'object' && !Array.isArray(customizations)
        ? customizations
        : {};
    localStorage.setItem('reportCustomizations', JSON.stringify(next));
    return next;
};

export const getVisibleReportColumns = (reportKey, catalog, accessOpts = buildReportColumnAccessOpts()) => {
    const stored = retrieveReportCustomizations()[reportKey];
    return normalizeReportColumns(stored, catalog, accessOpts);
};

export const updateLocalReportColumns = (reportKey, columns, catalog, accessOpts = buildReportColumnAccessOpts()) => {
    const prefs = retrieveReportCustomizations();
    const normalized = normalizeReportColumns(columns, catalog, accessOpts);
    persistReportCustomizations({ ...prefs, [reportKey]: normalized });
    return normalized;
};
